import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useApp } from "../context/AppContext";
import ProductCard from "../components/ProductCard";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Star, Heart, ShieldCheck, Truck, Repeat, Check, Tag, Ruler, Minus, Plus, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";

function getFabricDetails(fabric = "") {
  const f = (fabric || "").toLowerCase();
  const props = [];
  const care = ["Machine wash cold (max 30°C) with similar colours", "Wash inside-out to protect prints", "Do not bleach"];
  if (f.includes("cotton")) props.push("Breathable, skin-friendly natural cotton", "Pre-shrunk & bio-washed for a soft hand-feel");
  if (f.includes("supima")) props.push("Premium long-staple fibre — extra soft & pill-resistant");
  if (f.includes("combed")) props.push("Combed yarn for a smoother, stronger knit");
  if (f.includes("linen")) { props.push("Lightweight & airy — ideal for warm GCC weather", "Natural slub texture"); care.push("Warm iron while slightly damp"); }
  if (f.includes("denim")) { props.push("Durable woven denim with a structured drape"); care.push("Wash separately for the first few washes"); }
  if (f.includes("fleece")) { props.push("Brushed inner for cosy warmth", "Mid–heavy weight, great for layering"); care.push("Tumble dry low"); }
  if (f.includes("rayon") || f.includes("viscose")) { props.push("Fluid drape with a soft, silk-like touch"); care.push("Gentle cycle; dry flat in shade"); }
  if (f.includes("satin") || f.includes("georgette")) { props.push("Smooth, lustrous finish with elegant flow"); care.push("Hand wash or gentle cycle; cool iron"); }
  if (f.includes("spandex") || f.includes("nylon") || f.includes("polyester") || f.includes("stretch")) props.push("4-way stretch with excellent shape recovery", "Sweat-wicking & quick-dry");
  if (f.includes("terry")) props.push("Soft loop-back terry inner");
  if (f.includes("twill") || f.includes("poplin")) props.push("Crisp woven finish that holds its shape");
  if (f.includes("ripstop")) props.push("Rip-resistant woven grid for added durability");
  if (props.length === 0) props.push("Comfortable everyday fabric", "Soft, easy-wear hand-feel");
  return { props: [...new Set(props)], care: [...new Set(care)].slice(0, 5) };
}

function Stars({ value, size = 14 }) {
  return (
    <span className="inline-flex">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={size}
          className={i <= Math.round(value) ? "fill-[#D4AF37] text-[#D4AF37]" : "text-slate-300"}
        />
      ))}
    </span>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const { formatPrice, addToCart, toggleWishlist, wishlist, user, setAuthModalOpen } = useApp();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [logo, setLogo] = useState(true);
  const [qty, setQty] = useState(1);
  const [sizeChartOpen, setSizeChartOpen] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: "" });
  const [pincode, setPincode] = useState("");
  const [deliveryMsg, setDeliveryMsg] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/products/${id}`).then(({ data }) => {
      setProduct(data);
      setColor(data.colors?.[0] || "");
      api.get(`/products?category=${data.category}`).then(({ data: list }) =>
        setRelated(list.filter((p) => p.id !== data.id).slice(0, 4))
      );
    });
    api.get(`/products/${id}/reviews`).then(({ data }) => setReviews(data));
  }, [id]);

  if (!product) {
    return <div className="max-w-7xl mx-auto px-4 py-20 text-center text-slate-400">Loading...</div>;
  }

  const inWishlist = wishlist.includes(product.id);
  const discount = product.original_price
    ? Math.round((1 - product.price / product.original_price) * 100)
    : 0;

  const handleAdd = () => {
    if (!size) {
      toast.error("Please select a size");
      return;
    }
    addToCart(product, size, color, qty, logo);
  };

  const checkDelivery = () => {
    if (!pincode.trim()) return;
    setDeliveryMsg(`✓ Delivery available • Est. ${/^\d{6}$/.test(pincode) ? "3-5 days (India)" : "5-8 days (GCC)"}`);
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) {
      setAuthModalOpen(true);
      return;
    }
    try {
      await api.post(`/products/${id}/reviews`, { product_id: id, ...reviewForm });
      toast.success("Thanks! Your review will appear once it's approved.");
      const { data } = await api.get(`/products/${id}/reviews`);
      setReviews(data);
      const { data: p } = await api.get(`/products/${id}`);
      setProduct(p);
      setReviewForm({ rating: 5, comment: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const gallery = product.images?.length ? product.images : [product.images?.[0]];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="grid lg:grid-cols-2 gap-10">
        {/* gallery */}
        <div className="flex gap-3">
          <div className="hidden sm:flex flex-col gap-3">
            {gallery.map((img, i) => (
              <button
                key={i}
                onClick={() => setActiveImg(i)}
                className={`w-16 h-20 rounded-lg overflow-hidden border-2 ${activeImg === i ? "border-[#E63946]" : "border-transparent"}`}
              >
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
          <div className="flex-1 rounded-2xl overflow-hidden bg-slate-100 aspect-[3/4] relative" data-testid="product-detail-image">
            <img src={gallery[activeImg]} alt={product.name} className="w-full h-full object-cover" />
            <div className="absolute bottom-3 left-3 flex items-center gap-1.5 bg-white/85 backdrop-blur-sm rounded-full pl-1.5 pr-3 py-1 shadow">
              <img src="/brand/bull-mark.png" alt="" className="h-5 w-auto" />
              <span className="text-[11px] font-extrabold text-[#1D3557] tracking-wider">BULL CREATIONS</span>
            </div>
          </div>
        </div>

        {/* info */}
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">
            {product.gender} • {product.category}
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold" data-testid="product-detail-name">{product.name}</h1>

          <div className="flex items-center gap-2 mt-2">
            <Stars value={product.rating} />
            <span className="text-sm text-slate-500">{product.rating} ({product.num_reviews} reviews)</span>
          </div>

          <div className="flex items-baseline gap-3 mt-4">
            <span className="font-mono-price font-bold text-3xl" data-testid="product-detail-price">{formatPrice(product.price)}</span>
            {product.original_price && (
              <>
                <span className="text-lg text-slate-400 line-through font-mono-price">{formatPrice(product.original_price)}</span>
                <span className="text-[#E63946] font-bold">{discount}% OFF</span>
              </>
            )}
          </div>
          <p className="text-xs text-emerald-600 font-medium mt-1">Inclusive of all taxes</p>

          <p className="text-slate-600 dark:text-slate-300 mt-5 leading-relaxed">{product.description}</p>

          {/* colors */}
          {product.colors?.length > 0 && (
            <div className="mt-6">
              <div className="text-sm font-semibold mb-2">Color: <span className="text-slate-500">{color}</span></div>
              <div className="flex gap-2 flex-wrap">
                {product.colors.map((c) => (
                  <button
                    key={c}
                    onClick={() => setColor(c)}
                    data-testid={`color-option-${c.toLowerCase().replace(/\s/g, "-")}`}
                    className={`px-4 py-1.5 rounded-full border text-sm font-medium transition-all ${color === c ? "border-[#E63946] bg-[#E63946]/5 text-[#E63946]" : "border-slate-300 text-slate-600"}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* sizes */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold">Select Size</span>
              <button type="button" onClick={() => setSizeChartOpen(true)} data-testid="size-guide-button" className="text-xs text-[#E63946] font-semibold cursor-pointer flex items-center gap-1">
                <Ruler size={13} /> Size Guide
              </button>
            </div>
            <div className="flex gap-2 flex-wrap">
              {product.sizes.map((s) => (
                <button
                  key={s}
                  onClick={() => setSize(s)}
                  data-testid={`size-option-${s}`}
                  className={`w-12 h-12 rounded-lg border-2 font-semibold transition-all ${size === s ? "border-[#E63946] bg-[#E63946] text-white" : "border-slate-300 text-slate-700 hover:border-[#E63946]"}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* logo option */}
          <div className="mt-6">
            <div className="text-sm font-semibold mb-2">Bull Logo Print</div>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setLogo(true)}
                data-testid="logo-option-with"
                className={`flex items-center gap-2 px-4 py-3 rounded-xl border-2 text-sm font-semibold transition-all ${logo ? "border-[#E63946] bg-[#E63946]/5 text-[#E63946]" : "border-slate-300 text-slate-600"}`}
              >
                <img src="/brand/bull-mark.png" alt="" className="h-6 w-auto" />
                With Logo
              </button>
              <button
                onClick={() => setLogo(false)}
                data-testid="logo-option-without"
                className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 text-sm font-semibold transition-all ${!logo ? "border-[#1D3557] bg-[#1D3557]/5 text-[#1D3557]" : "border-slate-300 text-slate-600"}`}
              >
                Plain / No Logo
              </button>
            </div>
            <p className="text-xs text-slate-400 mt-2">
              {logo ? "Signature Bull Creations chest emblem — included free." : "A clean look with no printed logo."}
            </p>
          </div>

          {/* quantity */}
          <div className="mt-6">
            <div className="text-sm font-semibold mb-2">Quantity</div>
            <div className="inline-flex items-center border rounded-lg">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="qty-decrease" className="w-10 h-10 flex items-center justify-center hover:bg-slate-100"><Minus size={15} /></button>
              <span className="w-10 text-center font-semibold" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty((q) => Math.min(10, q + 1))} data-testid="qty-increase" className="w-10 h-10 flex items-center justify-center hover:bg-slate-100"><Plus size={15} /></button>
            </div>
          </div>

          {/* actions */}
          <div className="flex gap-3 mt-8">
            <Button
              data-testid="product-add-to-cart-button"
              onClick={handleAdd}
              className="flex-1 bg-[#E63946] hover:bg-[#d12836] h-13 py-6 text-base font-semibold"
            >
              Add to Bag
            </Button>
            <Button
              variant="outline"
              onClick={() => toggleWishlist(product.id)}
              data-testid="product-detail-wishlist"
              className="h-13 py-6 px-5 border-slate-300"
            >
              <Heart size={20} className={inWishlist ? "fill-[#E63946] text-[#E63946]" : ""} />
            </Button>
          </div>

          {/* virtual try-on */}
          <Link
            to={`/try-on?product=${product.id}`}
            data-testid="product-tryon-link"
            className="mt-3 flex items-center justify-center gap-2 h-12 rounded-md border-2 border-[#E63946] text-[#E63946] font-semibold hover:bg-[#E63946]/5 transition-colors"
          >
            <Sparkles size={18} /> Virtual Try-On — see it on you
          </Link>

          {/* pincode */}
          <div className="mt-6 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border">
            <div className="text-sm font-semibold mb-2">Check Delivery</div>
            <div className="flex gap-2">
              <input
                value={pincode}
                onChange={(e) => setPincode(e.target.value)}
                placeholder="India PIN or GCC postal code"
                data-testid="pincode-input"
                className="flex-1 px-3 py-2 rounded-lg border text-sm bg-white dark:bg-slate-900"
              />
              <Button onClick={checkDelivery} variant="outline" data-testid="check-delivery-button">Check</Button>
            </div>
            {deliveryMsg && <p className="text-sm text-emerald-600 mt-2 font-medium">{deliveryMsg}</p>}
          </div>

          {/* best offers */}
          <div className="mt-6 p-4 rounded-xl border border-dashed border-[#E63946]/40 bg-[#E63946]/5">
            <div className="flex items-center gap-2 font-semibold text-sm mb-2"><Tag size={16} className="text-[#E63946]" /> Best Offers</div>
            <ul className="space-y-1.5 text-sm text-slate-600 dark:text-slate-300">
              <li className="flex gap-2"><Check size={15} className="text-emerald-600 shrink-0 mt-0.5" /> Flat 10% off on your first order — auto-applied at checkout</li>
              <li className="flex gap-2"><Check size={15} className="text-emerald-600 shrink-0 mt-0.5" /> Earn 5% back as Bull Family reward points</li>
              <li className="flex gap-2"><Check size={15} className="text-emerald-600 shrink-0 mt-0.5" /> Free shipping across India & GCC on orders over ₹1999</li>
            </ul>
          </div>

          {/* material & care */}
          <div className="mt-6">
            <h3 className="font-display font-bold mb-3 flex items-center gap-2"><ShieldCheck size={18} className="text-[#1D3557] dark:text-[#D4AF37]" /> Material & Care</h3>
            <div className="rounded-xl border bg-slate-50 dark:bg-slate-800/50 p-4" data-testid="material-care-block">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Composition</span>
                <span className="text-sm font-semibold text-[#1D3557] dark:text-[#D4AF37]">{product.material || product.fabric || "Premium everyday fabric"}</span>
              </div>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-4">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1.5">Fabric Highlights</div>
                  <ul className="space-y-1.5 text-sm text-slate-600 dark:text-slate-300">
                    {getFabricDetails(product.fabric).props.map((p, i) => (
                      <li key={i} className="flex gap-2"><Check size={15} className="text-emerald-600 shrink-0 mt-0.5" /> {p}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1.5">Care Instructions</div>
                  <ul className="space-y-1.5 text-sm text-slate-600 dark:text-slate-300">
                    {getFabricDetails(product.fabric).care.map((c, i) => (
                      <li key={i} className="flex gap-2"><Repeat size={15} className="text-[#1D3557] dark:text-slate-400 shrink-0 mt-0.5" /> {c}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* highlights */}
          <div className="mt-6">
            <h3 className="font-display font-bold mb-3">Product Details</h3>
            <div className="grid grid-cols-2 gap-y-2 gap-x-6 text-sm">
              {[
                ["Fabric", product.fabric],
                ["Fit", product.fit],
                ["Category", product.category],
                ["Gender", product.gender],
                ["Pattern", "Solid / Printed"],
                ["Country of Origin", "India"],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-dashed py-1.5">
                  <span className="text-slate-400">{k}</span>
                  <span className="font-medium text-right">{v}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-3">Manufactured & packed by Bull Creations, Tiruppur, Tamil Nadu, India.</p>
          </div>

          {/* returns */}
          <Link to="/returns" data-testid="product-returns-link" className="mt-6 flex items-center justify-between gap-3 rounded-xl border bg-white dark:bg-slate-900 p-4 hover:border-[#E63946] transition-colors group">
            <div className="flex items-center gap-3">
              <Repeat size={18} className="text-[#E63946]" />
              <div>
                <div className="text-sm font-semibold">Easy 7-Day Returns & Exchange</div>
                <div className="text-xs text-slate-500">Free doorstep pickup • Refund to original payment or Bull Family wallet</div>
              </div>
            </div>
            <span className="text-[#E63946] text-sm font-semibold group-hover:translate-x-0.5 transition-transform">View policy →</span>
          </Link>
        </div>
      </div>

      {/* size chart dialog */}
      <Dialog open={sizeChartOpen} onOpenChange={setSizeChartOpen}>
        <DialogContent className="sm:max-w-md" data-testid="size-chart-dialog">
          <DialogHeader><DialogTitle>Size Guide (inches)</DialogTitle></DialogHeader>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800">
              <tr><th className="p-2 text-left">Size</th><th className="p-2">Chest</th><th className="p-2">Length</th><th className="p-2">Shoulder</th></tr>
            </thead>
            <tbody>
              {[["S", 38, 27, 17], ["M", 40, 28, 18], ["L", 42, 29, 18.5], ["XL", 44, 30, 19], ["XXL", 46, 31, 19.5]].map((r) => (
                <tr key={r[0]} className="border-t text-center">
                  <td className="p-2 font-semibold text-left">{r[0]}</td><td className="p-2">{r[1]}</td><td className="p-2">{r[2]}</td><td className="p-2">{r[3]}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-slate-400">Measurements are approximate. For kids items, sizes are shown by age (2–11Y).</p>
        </DialogContent>
      </Dialog>

      {/* reviews */}
      <section className="mt-16" data-testid="reviews-section">
        <h2 className="font-display text-2xl font-bold mb-6">Customer Reviews</h2>
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1">
            <div className="p-5 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border">
              <div className="text-4xl font-display font-extrabold">{product.rating}</div>
              <Stars value={product.rating} size={18} />
              <p className="text-sm text-slate-500 mt-1">Based on {product.num_reviews} reviews</p>
              <form onSubmit={submitReview} className="mt-5 space-y-3">
                <div className="text-sm font-semibold">Write a review</div>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <button
                      type="button"
                      key={i}
                      onClick={() => setReviewForm({ ...reviewForm, rating: i })}
                      data-testid={`review-star-${i}`}
                    >
                      <Star size={24} className={i <= reviewForm.rating ? "fill-[#D4AF37] text-[#D4AF37]" : "text-slate-300"} />
                    </button>
                  ))}
                </div>
                <Textarea
                  value={reviewForm.comment}
                  onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                  placeholder="Share your experience..."
                  data-testid="review-comment-input"
                  required
                />
                <Button type="submit" className="w-full bg-[#1D3557] hover:bg-[#162943]" data-testid="submit-review-button">
                  Submit Review
                </Button>
              </form>
            </div>
          </div>

          <div className="lg:col-span-2 space-y-4">
            {reviews.length === 0 ? (
              <p className="text-slate-400 py-8 text-center">No reviews yet. Be the first to review!</p>
            ) : (
              reviews.map((r) => (
                <div key={r.id} className="p-4 rounded-xl border bg-white dark:bg-slate-900" data-testid="review-item">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-[#1D3557] text-white flex items-center justify-center text-xs font-bold">
                        {r.user_name?.[0]?.toUpperCase()}
                      </div>
                      <span className="font-semibold text-sm">{r.user_name}</span>
                      <Check size={14} className="text-emerald-500" />
                    </div>
                    <Stars value={r.rating} />
                  </div>
                  <p className="text-sm text-slate-600 dark:text-slate-300 mt-2">{r.comment}</p>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="mt-16">
          <h2 className="font-display text-2xl font-bold mb-6">You May Also Like</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {related.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
